import {useId} from "react";
import {useLocale, useMessages} from "i18n";
import {ADVERTISE_PATH} from "sponsor/sponsorRoutes";
import type {SponsorAvailabilityDto} from "sponsor/SponsorApi";
import {
	AVAILABILITY_OUTLOOK_DAYS,
	availabilityOutlook,
	todayIso,
	type AvailabilityOutlook,
	type OutlookNextFree,
} from "./advertisingAvailability";
import {advertisingCopy, type AdvertisingAvailabilityCopy} from "./copy/advertising";
import {CtaNote, CtaRow, StartAdvertisingLink} from "./AdvertisingContent.styles";
import {
	AvailabilityCard,
	AvailabilityDay,
	AvailabilityDays,
	AvailabilityHead,
	AvailabilityLegend,
	AvailabilityLegendItem,
	AvailabilityLegendSwatch,
	AvailabilityLiveTag,
	AvailabilityNext,
	AvailabilityNextValue,
	AvailabilityTitle,
	CloserBand,
	CloserGrid,
	CloserHeading,
	CloserParagraph,
} from "./AdvertisingCloser.styles";

/**
 * The closing band: why booking here takes five minutes rather than a phone call (ui#1156), beside what
 * is actually free over the next fortnight (ui#1861).
 *
 * The row is the argument the paragraph makes, in evidence: "no sales calls" is a claim, and fourteen
 * cells showing which days are for sale right now is the same claim a reader can check. It is drawn from
 * the SAME availability read the terms strip above uses — the page reads the endpoint once, in
 * `AdvertisingContent`, and hands the result down — so the two bands can never disagree about what the
 * server said.
 *
 * Absent rather than empty when the read failed. `availabilityOutlook` returns `null` for a missing
 * read, which renders the band as it shipped before this ticket: the promise, the button and nothing
 * else. A skeleton or a row of zeros would be this page inventing a schedule.
 */
export function AdvertisingCloser({availability}: {availability: SponsorAvailabilityDto | null}) {
	const {locale} = useLocale();
	const {closer, cta} = advertisingCopy[locale];
	const headingId = useId();
	/*
	 * `todayIso()` reads the clock during render, which is normally the shape of a hydration mismatch.
	 * It is safe here for one specific reason: `availability` is `null` on the static render and on the
	 * first client render (the fetch is an effect), so this whole subtree is absent from both. By the
	 * time a date exists to compute, hydration is over.
	 */
	const outlook = availabilityOutlook(todayIso(), availability);

	return (
		<CloserBand aria-labelledby={headingId}>
			<CloserGrid>
				<div>
					<CloserHeading id={headingId}>{closer.heading}</CloserHeading>
					<CloserParagraph>{closer.paragraph}</CloserParagraph>
					<CtaRow>
						<StartAdvertisingLink to={ADVERTISE_PATH}>{cta.label}</StartAdvertisingLink>
						<CtaNote>{cta.note}</CtaNote>
					</CtaRow>
				</div>
				{outlook !== null && <AvailabilityRow outlook={outlook} copy={closer.availability}/>}
			</CloserGrid>
		</CloserBand>
	);
}

/**
 * The fourteen-day row.
 *
 * Every date it prints is composed from `sponsor.calendar`'s own month and weekday lists rather than
 * from `toLocaleDateString`, which would put English names on the Romanian page — the same rule
 * `SponsorCalendar` follows, and the reason `availabilityOutlook` returns numbers instead of a
 * formatted string.
 */
function AvailabilityRow(
	{outlook, copy}: {outlook: AvailabilityOutlook; copy: AdvertisingAvailabilityCopy},
) {
	const calendar = useMessages().sponsor.calendar;
	const titleId = useId();
	const dateOf = (weekday: number, dayOfMonth: number, month: number) =>
		copy.onDate(calendar.weekdaysLong[weekday], dayOfMonth, calendar.months[month - 1]);

	return (
		<AvailabilityCard aria-labelledby={titleId}>
			<AvailabilityHead>
				<AvailabilityTitle id={titleId}>{copy.title(AVAILABILITY_OUTLOOK_DAYS)}</AvailabilityTitle>
				<AvailabilityLiveTag>{copy.liveLabel}</AvailabilityLiveTag>
			</AvailabilityHead>
			<AvailabilityDays aria-label={copy.aria(AVAILABILITY_OUTLOOK_DAYS)}>
				{outlook.days.map(day => (
					/*
					 * The full date and its state as text, because the visible cell is a bare day number:
					 * "3" alone tells a screen reader nothing about which month it is in or whether it is
					 * for sale. `dayAria` and the two legend words are the calendar's own, so this row and
					 * the booking grid say the same thing about the same day.
					 */
					<AvailabilityDay
						key={day.date}
						$free={day.free}
						aria-label={calendar.dayAria(
							dateOf(day.weekday, day.dayOfMonth, day.month),
							day.free ? calendar.legend.available : calendar.legend.booked,
						)}
					>
						{day.dayOfMonth}
					</AvailabilityDay>
				))}
			</AvailabilityDays>
			{/* `aria-hidden`: every cell above already carries its own state in its accessible name, so
			    the legend is a visual key rather than information a screen reader is missing. */}
			<AvailabilityLegend aria-hidden="true">
				<AvailabilityLegendItem>
					<AvailabilityLegendSwatch $free={true}/>
					{calendar.legend.available}
				</AvailabilityLegendItem>
				<AvailabilityLegendItem>
					<AvailabilityLegendSwatch $free={false}/>
					{calendar.legend.booked}
				</AvailabilityLegendItem>
			</AvailabilityLegend>
			<AvailabilityNext>
				{outlook.nextFree === null
					? copy.noneFree(AVAILABILITY_OUTLOOK_DAYS)
					: <>
						{copy.nextFreeLabel}{" "}
						<AvailabilityNextValue>{nextFreeText(outlook.nextFree, copy, dateOf)}</AvailabilityNextValue>
					</>}
			</AvailabilityNext>
		</AvailabilityCard>
	);
}

/**
 * How the next free day is named: "today", "tomorrow", or the date.
 *
 * The two words rather than a date for the two cases a buyer reads as "now" — a page that answered
 * "Wednesday 2 September" on the second of September would be making the reader do arithmetic to learn
 * that they can book immediately, which is the one thing this row is trying to tell them.
 */
function nextFreeText(
	nextFree: OutlookNextFree,
	copy: AdvertisingAvailabilityCopy,
	dateOf: (weekday: number, dayOfMonth: number, month: number) => string,
): string {
	if (nextFree.today) {
		return copy.today;
	}
	if (nextFree.tomorrow) {
		return copy.tomorrow;
	}
	return dateOf(nextFree.weekday, nextFree.dayOfMonth, nextFree.month);
}
